import { outlastsPeriod } from "./billingActions";
import { parseBillingDate } from "./billingDates";
import { getPlanPrice } from "../../pages/auth/register/planPricing";

// What choosing a plan on the Subscription page does, by the rules product set
// on 2026-10-15. Both flows follow them, and the backend applies the same ones
// (its answers stay authoritative: the page reads them back):
//
// - A bigger packet (an upgrade) changes now. The difference between the two
//   monthly charges is charged today, in full, and the new price is billed
//   from the next billing date: Standard $99 → Premium $249 is $150 today,
//   then $249 a month. An annual plan keeps its commitment's end; a monthly
//   plan moved onto an annual one is committed from the start of this month.
// - The same packet, monthly → annual, starts when the month paid for ends.
// - A smaller packet (a downgrade) waits for the contract to end: the month
//   paid for, or an annual plan's 12 months. It is offered only while the
//   organization's active workspaces, and the active projects in each, fit the
//   smaller plan; otherwise they are archived first.
// - Annual → monthly isn't offered while the commitment runs past this month,
//   whatever the subscription's status, and in its last month waits for it to
//   end. A plan still on an old price billed once a year has no commitment
//   end: its year is the period, and the change waits for that.
// - During a trial every change is made now, and nothing is charged.
// - Packets are ranked by their monthly price, as the backend ranks them.
// - While the subscription is paused (a trial ended with no card), the change
//   is kept for when a card is added. The backend doesn't report it as a
//   pending change.
// - A plan still on an old once-a-year price moves to a bigger packet now,
//   and Stripe bills the unused part of the year against it; anything else
//   from it waits for the end of that year.
//
// - Enterprise terms our team offered (enterprise.js) are another plan on the
//   one packet, ranked by their monthly price like any packet: taken up from
//   any plan, and new terms from the ones in force. The same terms monthly →
//   annual are the same plan's other period.
//
// When what the page has doesn't settle it — a plan, a price or a date
// missing, or a packet not changed here, like Enterprise with no terms, or a
// move from Enterprise to a listed plan — the change is UNAVAILABLE rather
// than guessed.

export const PlanChangeKind = Object.freeze({
   /** The plan and the billing period in force. */
   CURRENT: "current",
   /** A bigger packet: now, with the difference charged today. */
   UPGRADE: "upgrade",
   /** Waits for the contract to end (`effectiveAt`). */
   SCHEDULED: "scheduled",
   /** During a trial: now, with nothing charged. */
   TRIAL: "trial",
   /** While the subscription is paused: applied once a card is added. */
   PAUSED: "paused",
   /** A smaller packet the organization doesn't fit: archive first. */
   OVER_LIMITS: "overLimits",
   /**
    * A smaller packet after the subscription was cancelled: only our team makes
    * that change (the backend answers DOWNGRADE_REQUIRES_SUPPORT).
    */
   NEEDS_SUPPORT: "needsSupport",
   /** Annual → monthly while the commitment runs past this month. */
   COMMITTED: "committed",
   /** Can't be worked out from what the page has (`reason`): not offered. */
   UNAVAILABLE: "unavailable",
   /**
    * Now, as the backend does it today, with the difference prorated onto the
    * next invoice. Every change, while the rules are switched off
    * (planDowngrades.js).
    */
   NOW: "now"
});

/** Why a change is UNAVAILABLE. */
export const UnavailableReason = Object.freeze({
   /** A packet changed only by our team, such as Enterprise. */
   UNSUPPORTED: "unsupported",
   /** A plan, a price or a date the page needs is missing, or out of date. */
   MISSING_DATA: "missingData"
});

/** Whether the kind can be chosen. */
export const isPlanChangeOffered = (kind) =>
   kind === PlanChangeKind.UPGRADE ||
   kind === PlanChangeKind.SCHEDULED ||
   kind === PlanChangeKind.TRIAL ||
   kind === PlanChangeKind.PAUSED ||
   kind === PlanChangeKind.NOW;

// Packets our team changes, never this page.
const MANAGED_PACKETS = new Set(["Enterprise", "Internal"]);

/** Whether a packet is changed on the Subscription page (not Enterprise or Internal). */
export const isSelfServicePacket = (name) => Boolean(name) && !MANAGED_PACKETS.has(name);

// Enterprise terms (enterprise.js, enterprisePlanOf) are taken up here, from a
// listed plan or from other terms. Leaving Enterprise for a listed plan is
// still our team's.
const isTermsChange = (current, target) =>
   Boolean(target?.terms) && (Boolean(current?.terms) || isSelfServicePacket(current?.name));

// Whether two plans hold the same terms: always, for packets, which have none;
// for Enterprise terms, the same `termsId`. Terms without one match nothing,
// rather than every other set without one.
const isSameTerms = (a, b) =>
   a?.terms || b?.terms ? Boolean(a?.termsId) && a.termsId === b?.termsId : true;

// A packet's rank is its monthly price, as the backend has it (a bigger packet
// costs more a month). Null for one without a price to rank by.
const rankOf = (plan) =>
   typeof plan?.monthlyPrice === "number" && plan.monthlyPrice > 0 ? plan.monthlyPrice : null;

const roundCents = (amount) => Math.round(amount * 100) / 100;

// 0, or no number, is no cap.
const isCapped = (limit) => typeof limit === "number" && limit > 0;

const unavailable = (reason) => ({ kind: PlanChangeKind.UNAVAILABLE, reason });

/**
 * Whether the organization's active workspaces, and the active projects in each,
 * fit a plan. `usage` is `{ workspaces: [{ id, name, projects }] }`, the active
 * ones only; without it the answer is yes, and the server decides.
 */
export const fitsPlan = (plan, usage) => {
   const workspaces = usage?.workspaces;
   if (!plan || !Array.isArray(workspaces)) return true;
   if (isCapped(plan.workspaceLimit) && workspaces.length > plan.workspaceLimit) return false;
   return !(
      isCapped(plan.projectDashboardLimit) &&
      workspaces.some((workspace) => workspace.projects > plan.projectDashboardLimit)
   );
};

/**
 * What choosing `target` does, from the plan in force.
 *
 * @param {Object} change
 * @param {{plan: Object, isMonthly: boolean}} change.current  the packet and period in force
 * @param {{plan: Object, isMonthly: boolean}} change.target   the packet and period chosen
 * @param {Object} change.subscription  `{ subscriptionStatus, currentPeriodStart,
 *    currentPeriodEnd, commitmentEnd, isCancelled }` (the billing status, or the old
 *    flow's read through `oldFlowCommitment`, with the period's start from the
 *    subscription read; `isCancelled` whether it is set to end)
 * @param {Object} [change.usage]  the active workspaces and their projects (fitsPlan)
 * @param {number} [change.now]  the time to judge dates by
 * @returns {{kind: string, chargeToday?: number, effectiveAt?: string, reason?: string,
 *    oldYearlyPrice?: boolean}}
 */
export function resolvePlanChange({ current, target, subscription, usage, now = Date.now() }) {
   if (!current?.plan?.id || !target?.plan?.id) return unavailable(UnavailableReason.MISSING_DATA);
   if (typeof current.isMonthly !== "boolean" || typeof target.isMonthly !== "boolean") {
      return unavailable(UnavailableReason.MISSING_DATA);
   }

   // The same packet on the same terms: two sets of Enterprise terms are two
   // plans, as the backend weighs them (PlanChangeService).
   const isSamePacket = current.plan.id === target.plan.id && isSameTerms(current.plan, target.plan);
   if (isSamePacket && current.isMonthly === target.isMonthly) return { kind: PlanChangeKind.CURRENT };

   if (
      !isTermsChange(current.plan, target.plan) &&
      (!isSelfServicePacket(current.plan.name) || !isSelfServicePacket(target.plan.name))
   ) {
      return unavailable(UnavailableReason.UNSUPPORTED);
   }
   const currentRank = rankOf(current.plan);
   const targetRank = rankOf(target.plan);
   if (!currentRank || !targetRank) return unavailable(UnavailableReason.MISSING_DATA);

   const isUpgrade = !isSamePacket && targetRank > currentRank;
   const isDowngrade = !isSamePacket && targetRank < currentRank;

   // A cancelled subscription moves to a smaller packet only through our team,
   // in a trial too. The backend checks this first, before the limits.
   if (isDowngrade && subscription?.isCancelled) return { kind: PlanChangeKind.NEEDS_SUPPORT };
   if (isDowngrade && !fitsPlan(target.plan, usage)) return { kind: PlanChangeKind.OVER_LIMITS };

   if (subscription?.subscriptionStatus === "trialing") return { kind: PlanChangeKind.TRIAL };
   if (subscription?.subscriptionStatus === "paused") return { kind: PlanChangeKind.PAUSED };

   // Everything else is timed by the period, and an annual plan by its
   // commitment: dates that are missing, or already past, settle nothing.
   const periodEnd = parseBillingDate(subscription?.currentPeriodEnd);
   if (!periodEnd || periodEnd.valueOf() <= now) return unavailable(UnavailableReason.MISSING_DATA);

   const isOldYearlyPrice =
      !current.isMonthly &&
      isYearlyPeriod(subscription?.currentPeriodStart, subscription?.currentPeriodEnd);

   // The year is paid for: no per-month difference, Stripe prorates the year.
   if (isOldYearlyPrice && isUpgrade) {
      return { kind: PlanChangeKind.UPGRADE, chargeToday: null, oldYearlyPrice: true };
   }
   let contractEnd = subscription.currentPeriodEnd;
   if (!current.isMonthly && !isOldYearlyPrice) {
      const commitmentEnd = parseBillingDate(subscription?.commitmentEnd);
      if (!commitmentEnd || commitmentEnd.valueOf() <= now) {
         return unavailable(UnavailableReason.MISSING_DATA);
      }
      contractEnd = subscription.commitmentEnd;
   }

   // Annual → monthly, whichever packet and whatever the status: not while the
   // commitment runs past this month, and then at its end.
   if (!current.isMonthly && target.isMonthly) {
      if (!isOldYearlyPrice && outlastsPeriod(contractEnd, subscription.currentPeriodEnd)) {
         return { kind: PlanChangeKind.COMMITTED };
      }
      return { kind: PlanChangeKind.SCHEDULED, effectiveAt: contractEnd };
   }

   if (isUpgrade) {
      const currentPrice = getPlanPrice(current.plan, !current.isMonthly);
      const targetPrice = getPlanPrice(target.plan, !target.isMonthly);
      if (typeof currentPrice !== "number" || typeof targetPrice !== "number") {
         return unavailable(UnavailableReason.MISSING_DATA);
      }
      return {
         kind: PlanChangeKind.UPGRADE,
         chargeToday: Math.max(0, roundCents(targetPrice - currentPrice))
      };
   }

   // A downgrade waits for the contract: an annual plan's commitment, a monthly
   // plan's month. The same packet, monthly → annual, waits for the month.
   return { kind: PlanChangeKind.SCHEDULED, effectiveAt: contractEnd };
}

/** The archive-first explanation for a plan the organization doesn't fit. */
export const describeOverLimits = (plan) => {
   const workspaces = isCapped(plan?.workspaceLimit)
      ? `${plan.workspaceLimit} active ${plan.workspaceLimit === 1 ? "workspace" : "workspaces"}`
      : "your active workspaces";
   const projects = isCapped(plan?.projectDashboardLimit)
      ? `, with up to ${plan.projectDashboardLimit} active ${
           plan.projectDashboardLimit === 1 ? "project" : "projects"
        } in each`
      : "";
   return `The ${plan?.name ?? "new"} plan includes ${workspaces}${projects}. Archive the workspaces and projects you don't need to fit it, then choose this plan. Nothing archived is deleted.`;
};

const DAY_MS = 24 * 60 * 60 * 1000;

// A period paid for month by month. A longer one is a plan still on an old
// price billed once a year, whose change Stripe bills at once instead.
const MAX_MONTH_DAYS = 32;

/** Whether a period is longer than a month: an old price billed once a year. */
export const isYearlyPeriod = (periodStart, periodEnd) => {
   const start = parseBillingDate(periodStart);
   const end = parseBillingDate(periodEnd);
   return Boolean(start && end && end.valueOf() - start.valueOf() > MAX_MONTH_DAYS * DAY_MS);
};

/**
 * What changing plan now adds to the next invoice, or takes off it, the way
 * Stripe prorates a change today (`create_prorations`): the new plan for the
 * time left in the period, less the unused part of the current one. The new
 * price itself is billed from the end of the period.
 *
 * An estimate: Stripe counts to the second, and tax and a promo code come on
 * top. Null when the period isn't known, isn't a month, or is already over.
 *
 * @returns {{daysLeft: number, newPart: number, unusedPart: number,
 *    difference: number, nextInvoice: number} | null}
 */
export function estimateProration({ currentPrice, newPrice, periodStart, periodEnd, now = Date.now() }) {
   const start = parseBillingDate(periodStart);
   const end = parseBillingDate(periodEnd);
   if (!start || !end || typeof currentPrice !== "number" || typeof newPrice !== "number") {
      return null;
   }

   const periodMs = end.valueOf() - start.valueOf();
   const leftMs = end.valueOf() - now;
   if (periodMs <= 0 || periodMs > MAX_MONTH_DAYS * DAY_MS || leftMs <= 0 || leftMs > periodMs) {
      return null;
   }

   const share = leftMs / periodMs;
   const newPart = roundCents(newPrice * share);
   const unusedPart = roundCents(currentPrice * share);
   const difference = roundCents(newPart - unusedPart);
   return {
      daysLeft: Math.max(1, Math.round(leftMs / DAY_MS)),
      newPart,
      unusedPart,
      difference,
      nextInvoice: roundCents(newPrice + difference)
   };
}

/** An amount with its cents: "$72.58", "$47.90". */
export const formatAmount = (amount) =>
   `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;


/**
 * Where an answer of the server — a billing status, or the old flow's
 * subscription read — holds `plan` on the period asked for (`isMonthly`):
 * "current" when it is in force, "pending" when it waits for the end of the
 * contract, or null. Read from the answer, not from the change the page worked
 * out: the server can settle on something else, e.g. completing an upgrade
 * paid meanwhile and then scheduling the new pick.
 *
 * Enterprise terms are in force once the answer names them as its
 * `enterprisePlan`: the packet stays Enterprise from one set to the next. The
 * `termsId` asked for is the one `plan` was made from (the offer), kept by the
 * caller, since the answer that holds them has no offer any more.
 */
export const planHeldIn = (answer, plan, isMonthly) => {
   if (!answer || !plan) return null;
   const asked = (packetId, isMonthlyPlan) => packetId === plan.id && isMonthlyPlan === isMonthly;
   const onTerms =
      !plan.terms || (Boolean(plan.termsId) && answer.enterprisePlan?.termsId === plan.termsId);
   if (
      onTerms &&
      (asked(answer.packetId, answer.isMonthly) ||
         (answer.planName === plan.name && answer.isMonthly === isMonthly))
   ) {
      return "current";
   }
   if (asked(answer.pendingPlanChange?.packetId, answer.pendingPlanChange?.isMonthly)) return "pending";
   return null;
};

/** The change as the answer shows it was made: scheduled, or made now. */
export const changeAsAnswered = (answer, plan, isMonthly, change) => {
   const held = planHeldIn(answer, plan, isMonthly);
   if (held === "pending" && change.kind !== PlanChangeKind.SCHEDULED) {
      return { ...change, kind: PlanChangeKind.SCHEDULED };
   }
   if (held === "current" && change.kind === PlanChangeKind.SCHEDULED) {
      return { ...change, kind: PlanChangeKind.NOW };
   }
   return change;
};
